import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { AlertTriangle } from "lucide-react";
import { useMemo } from "react";
import { errorMessage } from "../../../api/client";
import {
  messaging,
  mk,
  type NumberTexting,
  type TextingAdmin,
  type TextingStatus,
  textingQuery,
} from "../../../api/messaging";
import { useCanEdit } from "../../../auth/useMe";
import { DataTable } from "../../../components/DataTable";
import { RegistrationPill } from "../../../components/messaging/RegistrationPill";
import { TextingSettingsForm } from "../../../components/messaging/TextingSettingsForm";
import { toast } from "../../../components/toast";
import { Button, Card, EmptyState, ErrorState, Loading } from "../../../components/ui";
import { fmtPhone, relTime } from "../../../lib/format";
import { overallMessage } from "../../../lib/messaging";

export const Route = createFileRoute("/admin/messages/texting")({
  staticData: { crumb: { label: "Texting", parents: [] } },
  loader: ({ context }) => context.queryClient.prefetchQuery(textingQuery),
  component: TextingPage,
});

const col = createColumnHelper<NumberTexting>();

function TextingPage() {
  const qc = useQueryClient();
  const canEdit = useCanEdit();
  const q = useQuery(textingQuery);
  const refresh = useMutation({
    mutationFn: messaging.refresh,
    onSuccess: (status: TextingStatus) => {
      qc.setQueryData<TextingAdmin>(mk.texting, (d) => (d ? { ...d, status } : d));
      toast("Registration checked.");
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });

  const columns = useMemo(
    () => [
      col.accessor("e164", {
        header: "Number",
        cell: (c) => <span className="tnum whitespace-nowrap">{fmtPhone(c.getValue())}</span>,
      }),
      col.accessor((r) => r.brand ?? "", { id: "brand", header: "Brand" }),
      col.accessor((r) => r.owner?.name ?? "", {
        id: "owner",
        header: "Owner",
        cell: (c) => c.getValue() || <span className="text-ink-3">Shared</span>,
      }),
      col.accessor("state", {
        header: "Registration",
        cell: (c) => <RegistrationPill state={c.getValue()} size="sm" />,
      }),
      col.accessor("campaign_status", {
        header: "Campaign",
        cell: (c) =>
          c.getValue() ? <span className="font-mono text-caption text-ink-2">{c.getValue()}</span> : null,
      }),
      col.accessor("detail", {
        header: "Detail",
        enableSorting: false,
        cell: (c) => (
          <span className="block max-w-[22rem] text-caption text-ink-2">{c.getValue() ?? ""}</span>
        ),
      }),
      col.accessor("checked_at", {
        header: "Checked",
        cell: (c) => <span className="whitespace-nowrap text-ink-2">{relTime(c.getValue())}</span>,
      }),
    ],
    [],
  );

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { status, settings, opt_out_count } = q.data;
  const warning = overallMessage(status.overall);

  return (
    <div className="flex flex-col gap-4">
      <Card
        title={
          <span className="flex items-center gap-2.5">
            Registration
            <RegistrationPill state={status.overall} />
          </span>
        }
        actions={
          canEdit ? (
            <Button size="sm" busy={refresh.isPending} onClick={() => refresh.mutate()}>
              Check now
            </Button>
          ) : null
        }
      >
        {warning ? (
          <p
            role="status"
            className="mx-4 mt-3 flex items-start gap-2 rounded-lg bg-warning-soft px-3 py-2 text-ui text-warning"
          >
            <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
            {warning}
          </p>
        ) : null}
        <div className="mt-2">
          <DataTable
            data={status.numbers}
            columns={columns}
            getRowId={(r) => r.e164}
            empty={<EmptyState title="No numbers yet" description="Add numbers on the Numbers page." />}
          />
        </div>
        <p className="border-hairline border-t px-4 py-2.5 text-caption text-ink-3">
          {opt_out_count === 1 ? "1 number has" : `${opt_out_count} numbers have`} opted out of texts.
        </p>
      </Card>
      <TextingSettingsForm
        key={JSON.stringify(settings)}
        settings={settings}
        canEdit={canEdit}
        onSave={async (s) => {
          const next = await messaging.putTexting(s);
          qc.setQueryData(mk.texting, next);
          toast("Texting settings saved.");
        }}
      />
    </div>
  );
}
